import React, { useState } from 'react';
import { Clipboard, ClipboardCheck } from "lucide-react";

export const JsonViewer = ({ data, title }: { data: any; title: string }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy JSON", err);
        }
    };

    const syntaxHighlight = (key: string, value: any): React.ReactNode => {
        if (typeof value === "string") return <span className="text-green-600">"{value}"</span>;
        if (typeof value === "number") return <span className="text-blue-600">{value}</span>;
        if (typeof value === "boolean") return <span className="text-purple-600">{String(value)}</span>;
        if (value === null) return <span className="text-gray-500">null</span>;
        if (Array.isArray(value))
            return (
                <span className="text-yellow-600">
                    [
                    <>
                        {value.map((v, i) => (
                            <React.Fragment key={i}>
                                {syntaxHighlight("", v)}
                                {i < value.length - 1 ? ", " : ""}
                            </React.Fragment>
                        ))}
                    </>
                    ]
                </span>
            );
        if (typeof value === "object")
            return (
                <span className="text-gray-800">
                    {"{"}
                    {Object.entries(value).map(([k, v], i, arr) => (
                        <div key={k} className="ml-4">
                            <span className="text-red-600">"{k}"</span>: {syntaxHighlight(k, v)}
                            {i < arr.length - 1 ? "," : ""}
                        </div>
                    ))}
                    {"}"}
                </span>
            );
        return <span>{String(value)}</span>;
    };

    return (
        <div className="flex flex-col flex-1 min-w-0 min-h-0">
            <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-700 text-sm">{title}</h4>
                <button
                    onClick={handleCopy}
                    className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 transition"
                >
                    {copied ? (
                        <>
                            <ClipboardCheck className="h-3 w-3" />
                            Copied
                        </>
                    ) : (
                        <>
                            <Clipboard className="h-3 w-3" />
                            Copy
                        </>
                    )}
                </button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto bg-white p-4 rounded shadow-sm text-xs font-mono bg-gray-50">
                <pre className="whitespace-pre">{syntaxHighlight("", data)}</pre>
            </div>
        </div>
    );
};
